import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjsTZ from "../../../utils/dayjs";
import { requestDataWithParams } from "../../../helpers/API/RequestFactory";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const LEAVE_PROBE_START_YEAR = 2018;

function monthBounds(year, month) {
  const startMs = dayjsTZ(`${year}-${month}`, "YYYY-M").startOf("month").valueOf();
  const endMs = dayjsTZ(`${year}-${month}`, "YYYY-M")
    .endOf("month")
    .add(1, "day")
    .valueOf();
  return { startMs, endMs };
}

function yearWindowBounds(year) {
  return {
    startMs: dayjsTZ(`${year}-01-01`).startOf("year").valueOf(),
    endMs: dayjsTZ(`${year}-12-31`).endOf("year").add(1, "day").valueOf(),
  };
}

function yearsSpanBounds(years) {
  const sorted = [...years].sort((a, b) => a - b);
  const minY = sorted[0];
  const maxY = sorted[sorted.length - 1];
  return {
    startMs: dayjsTZ(`${minY}-01-01`).startOf("year").valueOf(),
    endMs: dayjsTZ(`${maxY}-12-31`).endOf("year").add(1, "day").valueOf(),
  };
}

function customBounds(fromDate, toDate) {
  const startMs = dayjsTZ(fromDate).startOf("day").valueOf();
  const endMs = dayjsTZ(toDate).endOf("day").add(1, "day").valueOf();
  return { startMs, endMs };
}

/**
 * Employee-scoped period data for the specs page.
 * Modes: month | custom | years (Lejet multi-year).
 * All API bounds are epoch milliseconds.
 */
export default function useEmployeeSpecsData(employeeId) {
  const now = dayjsTZ();
  const [mode, setMode] = useState("month");
  const [year, setYear] = useState(now.year());
  const [month, setMonth] = useState(now.month() + 1);
  const [selectedYears, setSelectedYears] = useState(() => [now.year()]);
  const [yearsWithData, setYearsWithData] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [leaveYearsLoading, setLeaveYearsLoading] = useState(false);
  const [clockings, setClockings] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [wages, setWages] = useState([]);
  const [prepayments, setPrepayments] = useState([]);
  const [employeeRequests, setEmployeeRequests] = useState([]);
  const [monthsWithData, setMonthsWithData] = useState(() => new Set());
  const activityCacheRef = useRef(new Map());
  const leaveYearsCacheRef = useRef(new Map());
  const fetchGenRef = useRef(0);

  const yearBounds = useMemo(() => yearWindowBounds(year), [year]);

  const { startMs, endMs } = useMemo(() => {
    if (mode === "custom" && fromDate && toDate) {
      return customBounds(fromDate, toDate);
    }
    if (mode === "years" || mode === "year") {
      const years =
        selectedYears?.length > 0 ? selectedYears : [year || now.year()];
      return yearsSpanBounds(years);
    }
    if (!month) {
      return monthBounds(year, dayjsTZ().month() + 1);
    }
    return monthBounds(year, month);
  }, [mode, year, month, fromDate, toDate, selectedYears, now]);

  const wageBounds = useMemo(() => {
    if (mode === "custom" && fromDate && toDate) {
      return customBounds(fromDate, toDate);
    }
    if ((mode === "years" || mode === "year") && selectedYears?.length) {
      return yearsSpanBounds(selectedYears);
    }
    return yearBounds;
  }, [mode, fromDate, toDate, selectedYears, yearBounds]);

  const periodLabel = useMemo(() => {
    if (mode === "custom" && fromDate && toDate) {
      return `${dayjsTZ(fromDate).format("DD/MM/YYYY")} – ${dayjsTZ(toDate).format("DD/MM/YYYY")}`;
    }
    if (mode === "years" || mode === "year") {
      const years = [...(selectedYears || [])].sort((a, b) => a - b);
      if (years.length === 0) return `${year}`;
      if (years.length === 1) return `${years[0]}`;
      return years.join(", ");
    }
    return `${MONTH_LABELS[(month || 1) - 1]} ${year}`;
  }, [mode, year, month, fromDate, toDate, selectedYears]);

  const refreshActivity = useCallback(
    async (targetYear) => {
      if (!employeeId) {
        setMonthsWithData(new Set());
        return;
      }
      const cacheKey = `${employeeId}:${targetYear}`;
      if (activityCacheRef.current.has(cacheKey)) {
        setMonthsWithData(activityCacheRef.current.get(cacheKey));
        return;
      }
      setActivityLoading(true);
      try {
        const results = await Promise.all(
          Array.from({ length: 12 }, (_, i) => {
            const m = i + 1;
            const { startMs: s, endMs: e } = monthBounds(targetYear, m);
            return requestDataWithParams("clocking", s, e, {
              employeeId,
              limit: 1,
            }).then((items) => ({
              m,
              has: Array.isArray(items) && items.length > 0,
            }));
          })
        );
        const next = new Set(results.filter((r) => r.has).map((r) => r.m));
        activityCacheRef.current.set(cacheKey, next);
        setMonthsWithData(next);
      } catch (err) {
        console.error("specs activity probe failed", err);
        setMonthsWithData(new Set());
      } finally {
        setActivityLoading(false);
      }
    },
    [employeeId]
  );

  const refreshLeaveYears = useCallback(async () => {
    if (!employeeId) {
      setYearsWithData([]);
      return;
    }
    if (leaveYearsCacheRef.current.has(employeeId)) {
      const cached = leaveYearsCacheRef.current.get(employeeId);
      setYearsWithData(cached);
      return cached;
    }
    setLeaveYearsLoading(true);
    try {
      const endYear = dayjsTZ().year() + 1;
      const startMs = dayjsTZ(`${LEAVE_PROBE_START_YEAR}-01-01`)
        .startOf("year")
        .valueOf();
      const endMs = dayjsTZ(`${endYear}-12-31`).endOf("year").add(1, "day").valueOf();
      const items = await requestDataWithParams("clocking", startMs, endMs, {
        employeeId,
        dayType: "HolidayRequest",
      });
      const years = new Set();
      (Array.isArray(items) ? items : []).forEach((el) => {
        const ts = el?.clockInDate || el?.createdAt;
        if (ts) years.add(dayjsTZ(ts).year());
      });
      const sorted = [...years].sort((a, b) => a - b);
      leaveYearsCacheRef.current.set(employeeId, sorted);
      setYearsWithData(sorted);
      return sorted;
    } catch (err) {
      console.error("specs leave years probe failed", err);
      setYearsWithData([]);
      return [];
    } finally {
      setLeaveYearsLoading(false);
    }
  }, [employeeId]);

  const fetchPeriodData = useCallback(async () => {
    if (!employeeId || !Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      return;
    }
    const gen = ++fetchGenRef.current;
    setLoading(true);
    try {
      const yearsForLeaves =
        mode === "years" || mode === "year"
          ? [...(selectedYears || [])].sort((a, b) => a - b)
          : null;

      const leavePromise =
        yearsForLeaves && yearsForLeaves.length > 0
          ? Promise.all(
              yearsForLeaves.map((y) => {
                const b = yearWindowBounds(y);
                return requestDataWithParams("clocking", b.startMs, b.endMs, {
                  employeeId,
                  dayType: "HolidayRequest",
                });
              })
            ).then((chunks) => chunks.flatMap((c) => (Array.isArray(c) ? c : [])))
          : requestDataWithParams("clocking", startMs, endMs, {
              employeeId,
              dayType: "HolidayRequest",
            });

      const [clockRes, leaveRes, wageRes, prepRes, reqRes] = await Promise.all([
        requestDataWithParams("clocking", startMs, endMs, { employeeId }),
        leavePromise,
        requestDataWithParams("wages", wageBounds.startMs, wageBounds.endMs, {
          employeeId,
        }),
        requestDataWithParams(
          "prepayments",
          wageBounds.startMs,
          wageBounds.endMs,
          { employeeId }
        ),
        requestDataWithParams("employeeRequests", startMs, endMs, {
          filterKey: "employeeId",
          filterValue: employeeId,
        }),
      ]);
      if (gen !== fetchGenRef.current) return;
      setClockings(Array.isArray(clockRes) ? clockRes : []);
      setLeaves(Array.isArray(leaveRes) ? leaveRes : []);
      setWages(Array.isArray(wageRes) ? wageRes : []);
      setPrepayments(Array.isArray(prepRes) ? prepRes : []);
      setEmployeeRequests(Array.isArray(reqRes) ? reqRes : []);
    } catch (err) {
      console.error("specs period fetch failed", err);
      if (gen !== fetchGenRef.current) return;
      setClockings([]);
      setLeaves([]);
      setWages([]);
      setPrepayments([]);
      setEmployeeRequests([]);
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  }, [
    employeeId,
    startMs,
    endMs,
    wageBounds.startMs,
    wageBounds.endMs,
    mode,
    selectedYears,
  ]);

  useEffect(() => {
    fetchPeriodData();
  }, [fetchPeriodData]);

  useEffect(() => {
    refreshActivity(year);
  }, [year, employeeId, refreshActivity]);

  const onYearChange = useCallback((nextYear, opts = {}) => {
    setYear(nextYear);
    if (opts.yearOnly) {
      setMode("years");
      setSelectedYears([nextYear]);
      setFromDate(null);
      setToDate(null);
    }
  }, []);

  const onMonthSelect = useCallback((nextMonth) => {
    setMode("month");
    setMonth(nextMonth);
    setFromDate(null);
    setToDate(null);
  }, []);

  const onRangeApply = useCallback((from, to) => {
    if (!from || !to) return false;
    if (dayjsTZ(from).isAfter(dayjsTZ(to), "day")) return false;
    setMode("custom");
    setFromDate(from);
    setToDate(to);
    setMonth(null);
    return true;
  }, []);

  const onYearToggle = useCallback((targetYear) => {
    setMode("years");
    setFromDate(null);
    setToDate(null);
    setSelectedYears((prev) => {
      const set = new Set(prev || []);
      if (set.has(targetYear)) {
        if (set.size === 1) return prev; // keep at least one
        set.delete(targetYear);
      } else {
        set.add(targetYear);
      }
      return [...set].sort((a, b) => a - b);
    });
    setYear(targetYear);
  }, []);

  const enterYearMode = useCallback(async () => {
    setMode("years");
    setFromDate(null);
    setToDate(null);
    const list = (await refreshLeaveYears()) || [];
    setSelectedYears((prev) => {
      const kept = (prev || []).filter((y) => list.includes(y));
      if (kept.length) return kept;
      if (list.includes(dayjsTZ().year())) return [dayjsTZ().year()];
      if (list.length) return [list[list.length - 1]];
      return [dayjsTZ().year()];
    });
  }, [refreshLeaveYears]);

  const enterMonthMode = useCallback(() => {
    setMode("month");
    setMonth((prev) => prev || dayjsTZ().month() + 1);
    setFromDate(null);
    setToDate(null);
  }, []);

  return {
    mode,
    year,
    month,
    selectedYears,
    yearsWithData,
    fromDate,
    toDate,
    startMs,
    endMs,
    periodLabel,
    loading,
    activityLoading,
    leaveYearsLoading,
    clockings,
    leaves,
    wages,
    prepayments,
    employeeRequests,
    monthsWithData,
    onYearChange,
    onMonthSelect,
    onRangeApply,
    onYearToggle,
    enterYearMode,
    enterMonthMode,
    refreshLeaveYears,
    setFromDate,
    setToDate,
    refreshActivity,
    refetch: fetchPeriodData,
  };
}
